<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import router from '@/router';
import { IonButton, IonIcon, IonInput } from '@ionic/vue';
import { calendarClearOutline, closeOutline, createOutline, linkOutline, locationOutline, timeOutline, downloadOutline, informationCircleOutline } from 'ionicons/icons';
import * as Yup from 'yup';
import QrcodeVue from 'qrcode.vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';

import ModalOne from '@/shared/components/ui/ModalOne.vue';
import TextInput from '@/shared/components/sign-up-forms/TextInput.vue';
import useCustomForm from '@/shared/composables/useCustomForm';
import useEvents from '@/modules/events/composables/useEvents';
import { Event, EventTicket } from '@/shared/interfaces/event';

// Props
const props = defineProps({
	email: { type: String, required: true },
	eventAllowEditTickets: { type: Boolean, required: true },
	eventDate: { type: String, required: true },
	eventId: { type: Number, required: true },
	eventName: { type: String, required: true },
	eventTickets: { type: Array as () => EventTicket[], required: false },
	hideQrCode: { type: Boolean, default: false },
	idx: { type: Number, required: true },
	name: { type: String, required: true },
	qrCode: { type: String, required: true },
	ticket: { type: Object as () => EventTicket, required: true },
	ticketDisabledEdit: { type: Boolean, required: true },
	ticketId: { type: String, required: true },
	ticketTypeColor: { type: String },
	ticketTypeName: { type: String },
	ticketTypeDescription: { type: String, default: '' },
	valueOnly: { type: Boolean, required: true, default: false },
	venue: { type: String, required: true },
	walletLinked: { type: Number as () => number | null | undefined },
});

// Init
const hideQrCode = ref(props.hideQrCode);
const { t, locale } = useI18n();
const emit = defineEmits(['onRefetchTickets']);
const isModalOpen = ref(false);
const qrCodeSizes = [150, 230, 80];
const qrCodeSize = ref(qrCodeSizes[0]); // Default size
const currentSizeIndex = ref(0);

const showHideModal = (openState: boolean) => {
	isModalOpen.value = openState;
};

const cycleQrCodeSize = () => {
	currentSizeIndex.value = (currentSizeIndex.value + 1) % qrCodeSizes.length;
	qrCodeSize.value = qrCodeSizes[currentSizeIndex.value];
};
const { downloadTicket } = useEvents();
const downloadingTicket = ref({ ticketIndex: -1, isDownloading: false });

// Using yup to generate a validation schema
const schema = Yup.object().shape({
	name: Yup.string().required(),
	email: Yup.string().required(),
});

// useCustomForm
const { isFormValid, isSuccess, onSubmit, onInputChange } = useCustomForm(schema, `/events/edit-ticket/${props.ticketId}`, 'post', 'ticket.saved', 'ticket.saving_error', 5000);

// Refetch ticket
watch(isSuccess, () => {
	if (isSuccess) {
		emit('onRefetchTickets');
		showHideModal(false);
		isSuccess.value = false;
	}
});

const showQrCode = () => {
	hideQrCode.value = false;
};

// Close modal and go to page
const closeModalAndGo = () => {
	showHideModal(false);
	router.push(`/friends/select?type=ticket&id=${props.ticketId}&eventId=${props.eventId}`);
};

// Start downloading ticket
const startDownloadingTicket = async (event: Event, ticket: EventTicket, idx: number) => {
	downloadingTicket.value = { ticketIndex: idx, isDownloading: true };
	await downloadTicket(event, ticket);
	downloadingTicket.value.isDownloading = false;
};

// Queue ticket computed properties
const isQueueTicket = computed(() => {
	return !!props.ticket.queue_activity_id;
});

const activityName = computed(() => {
	if (!isQueueTicket.value) return '';
	return props.ticket.queue_activity?.name || `Activity ${props.ticket.queue_activity_id}`;
});

const displayTicketType = computed(() => {
	return props.ticket.combo_ticket_type?.name || props.ticket.ticket_type?.name || props.ticketTypeName || '';
});

const formattedTimeslot = computed(() => {
	if (!isQueueTicket.value) return '';
	if (!props.ticket.queue_slot?.booked_time_slot) return null;

	const timeslot = props.ticket.queue_slot.booked_time_slot;
	const bookedDate = props.ticket.queue_slot?.booked_date;
	const activity = props.ticket.queue_activity || props.ticket.queue_slot?.queue_activity;

	// Format the date if available
	let dateStr = '';
	if (bookedDate) {
		try {
			const date = new Date(bookedDate);
			const localeMap: Record<string, string> = {
				'en': 'en-US',
				'es': 'es-ES',
				'fr': 'fr-FR',
				'pt': 'pt-PT',
				'nl': 'nl-NL',
			};
			const intlLocale = localeMap[locale.value] || locale.value || 'en-US';

			dateStr = date.toLocaleDateString(intlLocale, {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});
		} catch {
			dateStr = bookedDate;
		}
	}

	// Check if timeslot is a UTC datetime string (e.g., "2025-10-31T09:00:00.000000Z")
	const isUtcDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(timeslot.trim());

	let timeStr = '';

	if (isUtcDateTime) {
		// Parse UTC datetime and convert to local time
		try {
			const utcDate = new Date(timeslot);
			if (!isNaN(utcDate.getTime())) {
				// Extract local time
				const localHours = utcDate.getHours().toString().padStart(2, '0');
				const localMinutes = utcDate.getMinutes().toString().padStart(2, '0');
				const startTime = `${localHours}:${localMinutes}`;

				// Calculate end time from cycle duration
				if (activity && activity.cycle_duration_minutes) {
					const endDate = new Date(utcDate.getTime() + activity.cycle_duration_minutes * 60000);
					const endHours = endDate.getHours().toString().padStart(2, '0');
					const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
					const endTime = `${endHours}:${endMinutes}`;

					timeStr = `${startTime} - ${endTime}`;
				} else {
					timeStr = startTime;
				}
			}
		} catch (error) {
			console.error('Error parsing UTC datetime:', error);
		}
	} else {
		// If timeslot is a simple time string like "10:00", use it directly
		const simpleTimePattern = /^(\d{1,2}:\d{2})(?::\d{2})?$/;
		const simpleMatch = timeslot.trim().match(simpleTimePattern);
		if (simpleMatch) {
			const startTime = simpleMatch[1]; // Already in HH:MM format

			// Calculate end time from cycle duration
			if (activity && activity.cycle_duration_minutes) {
				const [hours, minutes] = startTime.split(':').map(Number);
				const startDate = new Date();
				startDate.setHours(hours, minutes, 0, 0);

				// Add cycle duration
				const endDate = new Date(startDate.getTime() + activity.cycle_duration_minutes * 60000);

				const endHours = endDate.getHours().toString().padStart(2, '0');
				const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
				const endTime = `${endHours}:${endMinutes}`;

				timeStr = `${startTime} - ${endTime}`;
			} else {
				timeStr = startTime;
			}
		} else {
			// If timeslot contains a date-time format, extract the time portion
			const allTimeMatches = timeslot.match(/(\d{1,2}:\d{2})(?::\d{2})?/g);

			if (allTimeMatches && allTimeMatches.length >= 2) {
				// Already has both times (e.g., "10:00-11:00" or date-time format)
				const startTime = allTimeMatches[allTimeMatches.length - 2].substring(0, 5); // Keep only HH:MM
				const endTime = allTimeMatches[allTimeMatches.length - 1].substring(0, 5); // Keep only HH:MM
				timeStr = `${startTime} - ${endTime}`;
			} else if (allTimeMatches && allTimeMatches.length === 1) {
				// Single time found in date-time string
				const startTime = allTimeMatches[0].substring(0, 5); // Keep only HH:MM

				// Calculate end time from cycle duration
				if (activity && activity.cycle_duration_minutes) {
					const [hours, minutes] = startTime.split(':').map(Number);
					const startDate = new Date();
					startDate.setHours(hours, minutes, 0, 0);

					// Add cycle duration
					const endDate = new Date(startDate.getTime() + activity.cycle_duration_minutes * 60000);

					const endHours = endDate.getHours().toString().padStart(2, '0');
					const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
					const endTime = `${endHours}:${endMinutes}`;

					timeStr = `${startTime} - ${endTime}`;
				} else {
					timeStr = startTime;
				}
			} else {
				// Fallback: return as is
				timeStr = timeslot;
			}
		}
	}

	// Combine date and time if both are available
	if (dateStr && timeStr) {
		return `${dateStr} • ${timeStr}`;
	} else if (timeStr) {
		return timeStr;
	}

	return timeslot;
});

const isUnbooked = computed(() => {
	return isQueueTicket.value && !props.ticket.queue_slot;
});

const queueTypeDisplay = computed(() => {
	if (!isQueueTicket.value) return '';
	const queueType = props.ticket.queue_slot?.queue_type || props.ticket.queue_type;
	if (queueType === 'fastlane') {
		return t('queue.priority_pass');
	}
	return t('queue.regular_pass');
});

const handleBookNow = () => {
	if (!isUnbooked.value || !props.ticket.queue_activity_id) return;

	// Store event ticket ID(s) in sessionStorage (similar to QueueTicketsBuyPage)
	const ticketIds = [props.ticket.id];
	sessionStorage.setItem('booking_event_ticket_ids', JSON.stringify(ticketIds));

	// Navigate to my-queues page
	router.push('/my-queues');
};
</script>

<template>
	<div>
		<ModalOne :title="t('components.ticket_detail_item.edit_ticket')" :is-open="isModalOpen">
			<template #content>
				<form id="changePasswordForm" :validation-schema="schema" @submit="onSubmit">
					<div class="relative mt-6">
						<div>
							<ion-input type="text" hidden="hidden" name="eventTicketId" :value="props.ticketId" />
						</div>
						<div class="flex items-center relative">
							<TextInput id="input-field" class="mr-2" wrapper-class="flex flex-wrap justify-left item-center mb-3 border border-solid focus-within:background-white-15" type="text" name="name" :placeholder="t('form.name')" :value="props.name" @inputChanged="onInputChange" />
							<ion-button class="go-back-button" @click="closeModalAndGo">
								<ion-icon :icon="require('@/statics/svg/people-add.svg')" class="w-[50px] h-[50px] cursor-pointer"></ion-icon>
							</ion-button>
						</div>
						<div class="relative">
							<TextInput id="input-field" wrapper-class="flex flex-wrap justify-left item-center mb-3 border border-solid focus-within:background-white-15" type="text" name="email" :placeholder="t('form.email')" :value="props.email" @inputChanged="onInputChange" />
						</div>
						<ion-button :disabled="!isFormValid" expand="full" shape="round" type="submit">
							{{ t('components.ticket_detail_item.edit_ticket') }}
						</ion-button>
					</div>
				</form>
			</template>

			<template #actions>
				<div class="flex items-center justify-center">
					<div class="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-white">
						<ion-icon class="text-[38px] text-black cursor-pointer" :icon="closeOutline" @click="showHideModal(false)"></ion-icon>
					</div>
				</div>
			</template>
		</ModalOne>

		<div class="flex items-center justify-center mb-2">
			<ion-button v-if="!ticket.event?.delayed_delivery_datetime || (ticket.event?.delayed_delivery_datetime && eventTickets && eventTickets[0].qr_code)" class="hover:bg-white hover:rounded-full" size="large" shape="round" @click="startDownloadingTicket(ticket.event, ticket, idx)">
				<div class="flex items-center justify-center">
					<ion-spinner v-if="downloadingTicket.ticketIndex === idx && downloadingTicket.isDownloading" name="dots" class="text-dark-blue mr-2"></ion-spinner>
					<ion-icon v-else :icon="downloadOutline" class="text-dark-blue mr-2"></ion-icon>
					<span class="text-m2">
						{{ downloadingTicket.ticketIndex === idx && downloadingTicket.isDownloading ? t('events.tickets.tickets_detail.downloading') : t('events.tickets.tickets_detail.download') }}
					</span>
				</div>
			</ion-button>
		</div>

		<div :id="`ticket-${props.ticketId}`" class="bg-white rounded-md p-6 w-[300px]">
			<div class="flex items-center justify-center mb-2 py-6">
				<div v-if="hideQrCode" class="relative cursor-pointer" @click="showQrCode()">
					<div class="flex items-center justify-center w-full h-full bg-black/50">
						<span class="uppercase font-sourcesanspro-semibold text-center">{{ t('components.ticket_detail_item.press_to_show') }}</span>
					</div>
					<qrcode-vue v-if="!props.valueOnly" :value="props.qrCode" :level="'M'" :size="qrCodeSize" background="#E0E0E0" class="cursor-pointer transition-all duration-300" @click="cycleQrCodeSize" />
				</div>
				<div v-else>
					<qrcode-vue v-if="!props.valueOnly" :value="props.qrCode" :level="'M'" :size="qrCodeSize" background="#E0E0E0" class="cursor-pointer transition-all duration-300" @click="cycleQrCodeSize" />
					<div v-else class="flex flex-col items-center text-l break-all text-dark-blue/70 cursor-pointer transition-all duration-300" @click="cycleQrCodeSize">
						<span
							><b>{{ t('components.ticket_detail_item.code') }}</b></span
						>
						{{ props.qrCode }}
					</div>
				</div>
			</div>

			<div v-if="props.walletLinked" class="flex items-center justify-center">
				<div class="flex px-2 py-2 bg-black rounded-full font-sourcesanspro-semibolditalic text-xs2 text-white/70">
					<ion-icon :icon="linkOutline" class="mr-2 text-white"></ion-icon>
					{{ t('components.ticket_detail_item.your_wallet_linked') }}
				</div>
			</div>

			<div class="flex mt-6 items-center justify-center mb-1">
				<div class="flex flex-col items-center justify-center font-sourcesanspro-semibold text-dark-blue">
					<ion-button v-if="true || (!props.ticketDisabledEdit && props.eventAllowEditTickets)" @click="showHideModal(true)" class="w-[24px] h-[24px] ion-no-margin ion-no-padding inline-flex items-center" fill="clear">
						<ion-icon :icon="createOutline" class="text-dark-blue"></ion-icon>
					</ion-button>
					<div class="px-1 text-l2 text-center">{{ props.name }}</div>
				</div>
			</div>

			<div class="text-dark-blue/70">
				<!-- Queue Ticket Label -->
				<div v-if="isQueueTicket" class="flex flex-col items-center justify-center mb-2">
					<span class="font-sourcesanspro-semibold text-xs2 text-dark-blue/70">
						{{ t('queue.queue_ticket') }}
					</span>
				</div>
				<div class="flex flex-col items-center justify-center mb-2">
					<div :style="`background-color: ${props.ticketTypeColor !== undefined && $props.ticketTypeColor != '' ? props.ticketTypeColor : '#000000'}`" class="flex min-h-[37px] px-4 py-1 items-center justify-center rounded-md max-w-full">
						<span class="font-sourcesanspro-semibold text-base2 text-white text-center break-words px-2">
							{{ isQueueTicket ? activityName : props.ticketTypeName }}
						</span>
					</div>
				</div>
				<!-- Queue type for queue tickets -->
				<div v-if="isQueueTicket && queueTypeDisplay" class="flex flex-col items-center justify-center mb-2">
					<span class="font-sourcesanspro-semibold text-xs2 text-dark-blue/70">
						{{ queueTypeDisplay }}
					</span>
				</div>
				<!-- Ticket type for queue tickets (event ticket type) -->
				<div v-if="isQueueTicket && displayTicketType" class="flex flex-col items-center justify-center mb-2">
					<span class="font-sourcesanspro-semibold text-xs2 text-dark-blue/70">
						{{ displayTicketType }}
					</span>
				</div>
				<!-- Timeslot or Unbooked status for queue tickets -->
				<div v-if="isQueueTicket" class="flex flex-col items-center justify-center mb-2">
					<span v-if="formattedTimeslot" class="font-sourcesanspro-semibold text-sm text-dark-blue">
						{{ formattedTimeslot }}
					</span>
					<span v-else-if="isUnbooked" class="font-sourcesanspro-semibold text-sm text-dark-blue/70">
						{{ t('queue.unbooked') }}
					</span>
				</div>
				<!-- Book now button for unbooked queue tickets -->
				<div v-if="isUnbooked" class="flex items-center justify-center mb-4">
					<ion-button class="hover:bg-white hover:rounded-full" size="default" shape="round" @click="handleBookNow">
						<span class="text-base">{{ t('queue.book_now') }}</span>
					</ion-button>
				</div>
				<div class="mb-2">
					<div class="font-sourcesanspro-semibold text-xs2 text-dark-blue leading-normal">
						{{ props.eventName }}
					</div>
				</div>
				<!-- Date, Time, and Venue - only show for non-queue tickets -->
				<template v-if="!isQueueTicket">
					<template v-if="!props.ticket.event?.hide_date_time">
						<div class="flex items-center justify-between mb-1">
							<div class="flex flex-col">
								<span class="font-sourcesanspro-bold text-base">{{ t('components.ticket_detail_item.date') }}</span>
								<span class="font-sourcesanspro-regular text-base2">
									{{ DateTime.fromFormat(props.eventDate, 'yyyy-MM-dd HH:mm:ss').toFormat('EEEE dd MMM, yyyy') }}
								</span>
							</div>
							<ion-icon :icon="calendarClearOutline" class="text-dark-blue"></ion-icon>
						</div>
						<div class="flex items-center justify-between mb-1">
							<div class="flex flex-col">
								<span class="font-sourcesanspro-bold text-base">{{ t('components.ticket_detail_item.time') }}</span>
								<span class="font-sourcesanspro-regular text-base2">
									{{ DateTime.fromFormat(props.eventDate, 'yyyy-MM-dd HH:mm:ss').toFormat('HH:mm a') }}
								</span>
							</div>
							<ion-icon :icon="timeOutline" class="text-dark-blue"></ion-icon>
						</div>
						<div v-if="props.ticketTypeDescription" class="flex items-center justify-between mb-1">
							<div class="flex flex-col max-w-[85%]">
								<span class="font-sourcesanspro-bold text-base">{{ t('components.ticket_detail_item.description') }}</span>
								<span class="font-sourcesanspro-regular text-base2 text-dark-blue/70">
									{{ props.ticketTypeDescription }}
								</span>
							</div>
							<ion-icon :icon="informationCircleOutline" class="text-dark-blue"></ion-icon>
						</div>
					</template>
					<div v-if="props.venue" class="flex items-center justify-between mb-1">
						<div class="flex flex-col">
							<span class="font-sourcesanspro-bold text-base">{{ t('components.ticket_detail_item.venue') }}</span>
							<span class="font-sourcesanspro-regular text-base2">{{ props.venue }}</span>
						</div>
						<ion-icon :icon="locationOutline" class="text-dark-blue"></ion-icon>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<style scoped></style>
